import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Button,
  Divider,
  CircularProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useAppTheme } from '../../contexts/ThemeContext';
import API from './Api';

const NotificationSettings = ({ showNotification }) => {
  const { palette, Colors } = useAppTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [prefs, setPrefs] = useState({
    // Channels
    emailNotifications: true,
    smsNotifications: false,

    // Reminders (scheduled)
    notifyBeforeProcessingDays: 1,
    notifyAfterProcessing: true,
    notifyBeforeGracePeriodEnds: true,
    notifyAfterPenaltyImposed: true,

    // Account-change alerts
    notifyOnBalanceEdit: true,
    notifyOnDepositEdit: true,
    notifyOnPaymentReceived: true,
    notifyOnLeaseExpiry: true,
    notifyOnMaintenanceUpdate: true,
  });

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const response = await API.company.getNotificationPreferences();
      const p = response.notification_preferences;
      setPrefs({
        emailNotifications: p.email_notifications,
        smsNotifications: p.sms_notifications,
        notifyBeforeProcessingDays: p.notify_before_processing_days,
        notifyAfterProcessing: p.notify_after_processing,
        notifyBeforeGracePeriodEnds: p.notify_before_grace_period_ends,
        notifyAfterPenaltyImposed: p.notify_after_penalty_imposed,
        notifyOnBalanceEdit: p.notify_on_balance_edit,
        notifyOnDepositEdit: p.notify_on_deposit_edit,
        notifyOnPaymentReceived: p.notify_on_payment_received,
        notifyOnLeaseExpiry: p.notify_on_lease_expiry,
        notifyOnMaintenanceUpdate: p.notify_on_maintenance_update,
      });
    } catch (err) {
      // Silently fall back to defaults if the endpoint isn't wired yet
      console.error('Failed to load notification preferences', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => (event) => {
    setPrefs((prev) => ({ ...prev, [key]: event.target.checked }));
  };

  const handleDaysChange = (event) => {
    const val = parseInt(event.target.value, 10);
    if (!isNaN(val) && val >= 0 && val <= 14) {
      setPrefs((prev) => ({ ...prev, notifyBeforeProcessingDays: val }));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await API.company.updateNotificationPreferences({
        email_notifications: prefs.emailNotifications,
        sms_notifications: prefs.smsNotifications,
        notify_before_processing_days: prefs.notifyBeforeProcessingDays,
        notify_after_processing: prefs.notifyAfterProcessing,
        notify_before_grace_period_ends: prefs.notifyBeforeGracePeriodEnds,
        notify_after_penalty_imposed: prefs.notifyAfterPenaltyImposed,
        notify_on_balance_edit: prefs.notifyOnBalanceEdit,
        notify_on_deposit_edit: prefs.notifyOnDepositEdit,
        notify_on_payment_received: prefs.notifyOnPaymentReceived,
        notify_on_lease_expiry: prefs.notifyOnLeaseExpiry,
        notify_on_maintenance_update: prefs.notifyOnMaintenanceUpdate,
      });
      showNotification('Notification preferences saved successfully');
    } catch (err) {
      showNotification('Failed to save notification preferences', 'error');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // ── Shared styles ─────────────────────────────────────────────────────────
  const cardSx = {
    mb: 3,
    backgroundColor: palette.background.tertiary,
    border: `1px solid ${palette.border}`,
    boxShadow: 'none',
  };

  const switchSx = {
    '& .MuiSwitch-switchBase.Mui-checked': { color: Colors.primary.main },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: Colors.primary.main,
    },
  };

  const SectionHeader = ({ icon: Icon, title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      <Icon sx={{ color: Colors.primary.main, fontSize: 20 }} />
      <Typography variant="h6" sx={{ color: palette.text.primary }}>
        {title}
      </Typography>
    </Box>
  );

  const NotificationRow = ({ label, description, settingKey }) => (
    <Box sx={{ mb: 1 }}>
      <FormControlLabel
        control={
          <Switch
            checked={prefs[settingKey]}
            onChange={handleToggle(settingKey)}
            sx={switchSx}
          />
        }
        label={
          <Typography sx={{ color: palette.text.primary, fontWeight: 500 }}>
            {label}
          </Typography>
        }
      />
      {description && (
        <Typography
          variant="body2"
          sx={{ color: palette.text.secondary, pl: 6, mt: -0.5 }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
        <CircularProgress sx={{ color: Colors.primary.main }} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ color: palette.text.primary, mb: 3 }}>
        Notification Preferences
      </Typography>

      {/* ── Communication Channels ────────────────────────────────────────── */}
      <Card sx={cardSx}>
        <CardContent>
          <SectionHeader icon={NotificationsActiveIcon} title="Communication Channels" />

          <NotificationRow
            label="Email Notifications"
            description="Send all notifications via email to the tenant's registered address."
            settingKey="emailNotifications"
          />
          <Divider sx={{ my: 1.5, borderColor: palette.border }} />
          <NotificationRow
            label="SMS Notifications"
            description="Send all notifications via text message to the tenant's phone number."
            settingKey="smsNotifications"
          />
        </CardContent>
      </Card>

      {/* ── Scheduled Reminders ───────────────────────────────────────────── */}
      <Card sx={cardSx}>
        <CardContent>
          <SectionHeader icon={NotificationsActiveIcon} title="Scheduled Reminders" />

          {/* Before processing — with configurable day count */}
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={prefs.notifyBeforeProcessingDays > 0}
                  onChange={(e) =>
                    setPrefs((prev) => ({
                      ...prev,
                      notifyBeforeProcessingDays: e.target.checked ? 1 : 0,
                    }))
                  }
                  sx={switchSx}
                />
              }
              label={
                <Typography sx={{ color: palette.text.primary, fontWeight: 500 }}>
                  Remind tenants before monthly processing date
                </Typography>
              }
            />
            <Typography variant="body2" sx={{ color: palette.text.secondary, pl: 6, mt: -0.5 }}>
              Tenants with an outstanding balance are notified ahead of time to settle before charges run.
            </Typography>
            {prefs.notifyBeforeProcessingDays > 0 && (
              <Box sx={{ pl: 6, mt: 1.5 }}>
                <TextField
                  size="small"
                  label="Days before processing"
                  value={prefs.notifyBeforeProcessingDays}
                  onChange={handleDaysChange}
                  type="number"
                  inputProps={{ min: 1, max: 14 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="caption" color="text.secondary">
                          day(s)
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: 200,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: palette.border },
                      '&:hover fieldset': { borderColor: Colors.primary.main },
                      backgroundColor: palette.background.secondary,
                    },
                    '& .MuiInputLabel-root': { color: palette.text.secondary },
                    '& .MuiInputBase-input': { color: palette.text.primary },
                  }}
                />
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 1.5, borderColor: palette.border }} />

          <NotificationRow
            label="Notify after monthly processing — negative balances"
            description="After charges run, notify tenants whose account balance goes negative (i.e. rent not covered)."
            settingKey="notifyAfterProcessing"
          />

          <Divider sx={{ my: 1.5, borderColor: palette.border }} />

          <NotificationRow
            label="Notify the day before grace period ends"
            description="Send a final reminder to tenants with unpaid rent 24 hours before the grace period expires and a penalty becomes due."
            settingKey="notifyBeforeGracePeriodEnds"
          />

          <Divider sx={{ my: 1.5, borderColor: palette.border }} />

          <NotificationRow
            label="Notify after a late-payment penalty is imposed"
            description="Tenants are informed immediately when a penalty charge has been added to their account."
            settingKey="notifyAfterPenaltyImposed"
          />
        </CardContent>
      </Card>

      {/* ── Account-Change Alerts ─────────────────────────────────────────── */}
      <Card sx={cardSx}>
        <CardContent>
          <SectionHeader icon={EditNoteIcon} title="Account & Transaction Alerts" />

          <NotificationRow
            label="Balance manually edited"
            description="Notify the tenant whenever a manager manually adjusts their account balance."
            settingKey="notifyOnBalanceEdit"
          />
          <Divider sx={{ my: 1.5, borderColor: palette.border }} />

          <NotificationRow
            label="Security deposit edited"
            description="Notify the tenant when their security deposit amount is updated."
            settingKey="notifyOnDepositEdit"
          />
          <Divider sx={{ my: 1.5, borderColor: palette.border }} />

          <NotificationRow
            label="Payment received"
            description="Confirm to the tenant when a rent or utility payment is recorded on their account."
            settingKey="notifyOnPaymentReceived"
          />
          <Divider sx={{ my: 1.5, borderColor: palette.border }} />

          <NotificationRow
            label="Lease approaching expiry"
            description="Notify both the tenant and the property manager when a lease is nearing its end date."
            settingKey="notifyOnLeaseExpiry"
          />
          <Divider sx={{ my: 1.5, borderColor: palette.border }} />

          <NotificationRow
            label="Maintenance request status update"
            description="Notify the tenant whenever the status of one of their maintenance requests changes."
            settingKey="notifyOnMaintenanceUpdate"
          />
        </CardContent>
      </Card>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ bgcolor: Colors.primary.main, '&:hover': { bgcolor: Colors.primary.dark } }}
        >
          {saving ? 'Saving…' : 'Save Preferences'}
        </Button>
      </Box>
    </Box>
  );
};

export default NotificationSettings;